import { useStore } from "effector-react";
import { useRouter } from "next/router";
import { useEffect } from "react";
import Notification from "@components/UI/Notification";
import Aims from "../../../features/Course/parts/course/Aims";
import Author from "../../../features/Course/parts/course/Author";
import Description from "../../../features/Course/parts/course/Description";
import CourseDetailsCard from "../../../features/CourseDetailsCard/view/entries";
import CourseReqs from "../../../features/Course/parts/course/CourseReqs";
import CourseTestimonials from "../../../features/Course/parts/course/CourseTestimonials";
import MainInfo from "../../../features/Course/parts/course/MainInfo";
import OtherAuthorCourses from "../../../features/Course/parts/course/OtherAuthorCourses";
import s from "./styles.module.sass";
import Image from "next/image";
import arrowSrc from "../../../../public/icons/arrowGrey.svg";
import { COURSE_STATUSES } from "../../../constants/COURSE_STATUSES";
import Head from "next/head";
import { useEvent } from "effector-react/scope";
import useAuthedUserCourse from "@hooks/useAuthedUserCourse";
import { MainPagesLayout } from "../../../containers/MainPagesLayout";
import CourseQuestionAndAnswer from "@components/UI/CourseQuestionAndAnswer";
import { getReviews } from "@features/Reviews/model/public";
import "../../../features/Course/model/init";
import "../../../features/Reviews/model/init";
import Content from "@features/Course/parts/course/Content/entries";
import { $course, $courseError } from "@features/Course/model/public";
import ModeratorBar from "@features/Course/parts/watchCourse/ModeratorBar";
import { prepareCourseSeo } from "@shared/functions/prepareCourseSeo";
import { calcFinalPrice } from "@shared/functions/calcFinalPrice";
import { prepareUrlWithHost } from "@shared/functions/makeUrlWithHost";
import { useWebAnalyticsContext } from "@shared/analytics/webAnalytics.contexts";
import { roundNumber } from "@shared/functions/roundNumber";
import { $courseAuthorInfo } from "@models/courses/public";
import { NeuralQA } from "@features/NeuralQAModal/model/typedef";
import checkAndPrepareRating from "@shared/functions/checkAndPrepareRating";

type CourseViewProps = {
	isPreviewMode: boolean;
};

const CourseView = ({ isPreviewMode }: CourseViewProps) => {
	const course = useStore($course);
	const { push } = useRouter();
	const author = useStore($courseAuthorInfo);
	const courseError = useStore($courseError);
	const { isCourseCanBeWatched } = useAuthedUserCourse();
	const getReviewsEvent = useEvent(getReviews);
	const { isModerator } = useAuthedUserCourse();
	const { viewProduct } = useWebAnalyticsContext();
	const { seoDescription, seoTitle } = prepareCourseSeo({
		title: course?.title || "",
		author: {
			name: author?.firstName || "",
			secondName: author?.lastName || "",
		},
		price: calcFinalPrice({
			cost: course?.cost || 0,
			discount: course?.discount || 0,
		}),
		description: course?.subtitle || "",
	});
	const aims = course?.audience?.what;
	const isAims = aims && aims[0].length !== 0;
	const createdSections = course?.sections;
	const reqs = course?.audience?.requirements;
	const isReqs = reqs && reqs[0].length !== 0;
	const router = useRouter();
	const preparedRating = checkAndPrepareRating(course?.rating || 0);

	useEffect(() => {
		if (!course) {
			return;
		}
		getReviewsEvent();
		viewProduct({
			id: course.id.toString(),
			name: course.title,
			price: roundNumber(course.cost),
			discount: course.discount,
			category: course.category?.title || "",
		});
		if (isCourseCanBeWatched && !isPreviewMode) {
			push(`/course/${course.url}`);
		}
	}, []);

	const toCourseEditing = () => {
		router.push(`/account/constructor/${course?.id}`);
	};

	const coursePath = prepareUrlWithHost(router.asPath);

	{
		/* Необходимо подменить контент с responder === expert на контент c responder === ai, backend отказался изменять ключ https://github.com/spiks/selfschool-backend/issues/1179*/
	}
	const qaWithoutAiResponse: NeuralQA[] | [] =
		course?.qa
			?.filter((qa) => {
				return qa.responder !== "ai";
			})
			.map((qa) => {
				return {
					...qa,
					responder: "expert",
				};
			}) || [];

	return course?.id ? (
		<MainPagesLayout>
			<Head>
				<title>{seoTitle}</title>
				<meta name="description" content={seoDescription} />
				<meta property="og:title" content={seoTitle} />
				<meta property="og:description" content={seoDescription} />
				<meta property="og:url" content={coursePath} />
				<meta property="og:image" content={course.coverUrl || ""} />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
				<link rel="canonical" href={coursePath} />
			</Head>
			{isModerator && course && isPreviewMode && (
				<div className={s.moderatorBarWrapper}>
					<div className={s.moderatorBarContainer}>
						<ModeratorBar courseId={course.id} courseStatus={course.status} courseTags={course?.tags} />
					</div>
				</div>
			)}
			{isPreviewMode && (
				<button className={s.backButton} onClick={toCourseEditing}>
					<div className={s.backArrowImageWrapper}>
						<Image src={arrowSrc} width={14} height={14} objectFit="fill" alt="<" />
					</div>
					Вернуться к созданию курса
				</button>
			)}
			{/* Authors - это массив, но изначально в компоненте был только один автор. Я полагаю, что это недоработка предыдущих разработчиков.
			В любом случае, это в текущей задаче это просто перенос логики наверх, а не изменение.
			*/}
			<MainInfo
				courseInfo={course}
				rating={preparedRating}
				author={
					author ?? {
						firstName: "Автор",
						lastName: "Не указан",
					}
				}
			/>
			<div className={s.restContentWrapper}>
				{isPreviewMode && course.status !== COURSE_STATUSES.PUBLISHED && (
					<div className={s.notificationWrapper}>
						<Notification
							type="error"
							title="Вы просматриваете черновик курса."
							text="Данный курс ещё не проходил проверку и не был опубликован. Обратите внимание, что видео и аудио материалы, находящиеся в обработке, не будут отображаться в предпросмотре курса до полного завершения этого процесса."
						/>
					</div>
				)}
				<CourseDetailsCard isPreviewMode={isPreviewMode} />
				{isAims && <Aims aims={course?.audience?.what ?? []} />}
			</div>
			<div className={s.otherContentWrapper}>
				{course.qa && course.qa.length !== 0 && (
					<CourseQuestionAndAnswer content={qaWithoutAiResponse} type="full" />
				)}

				<Description description={course.description} audienceTexts={course.audience?.who ?? []} />
				{createdSections?.length !== 0 && <Content sections={course.sections} />}
				{isReqs && <CourseReqs requirements={course.audience?.requirements ?? []} />}
				{/* Проверка на undefined обусловлена обратной совместимостью. Неясно, что у них на бэке с данными. */}
				{author !== null && <Author author={author} />}
				{!isPreviewMode && <CourseTestimonials />}
				<OtherAuthorCourses />
			</div>
		</MainPagesLayout>
	) : (
		<p className={s.text}>{courseError ? "Такого курса пока что нет" : ""}</p>
	);
};

export default CourseView;
